import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';

const CRITERIA_VOYAGEUR = [
  { id: 'proprete',      label: 'Propreté',             desc: 'État général du logement à votre arrivée',                          dbField: 'rating_cleanliness' },
  { id: 'conformite',    label: 'Conformité',            desc: 'Le logement correspondait-il aux photos et à la description ?',       dbField: 'rating_accuracy' },
  { id: 'communication', label: 'Communication',         desc: "Réactivité et clarté des échanges avec l'hôte",                      dbField: 'rating_communication' },
  { id: 'checkin',       label: 'Arrivée',               desc: 'Facilité du check-in et accueil',                                    dbField: null },
  { id: 'rapport',       label: 'Rapport qualité/prix',  desc: 'Le logement valait-il son prix ?',                                   dbField: 'rating_value' },
  { id: 'localisation',  label: 'Localisation',          desc: 'Situation géographique et accessibilité',                            dbField: 'rating_location' },
];

const CRITERIA_HOTE = [
  { id: 'respect',       label: 'Respect du logement',  desc: 'Le voyageur a-t-il pris soin du logement ?',                         dbField: null },
  { id: 'communication', label: 'Communication',         desc: 'Réactivité et clarté des échanges',                                  dbField: 'rating_communication' },
  { id: 'regles',        label: 'Respect des règles',    desc: 'Check-in/out respectés, règles de la maison suivies',                dbField: null },
  { id: 'recommend',     label: 'Recommandation',        desc: "Recommanderiez-vous ce voyageur à d'autres hôtes ?",                 dbField: null },
];

function StarRating({ value, onChange }) {
  const [hover, setHover] = useState(0);
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1, 2, 3, 4, 5].map(star => (
        <span key={star} onClick={() => onChange(star)}
          onMouseEnter={() => setHover(star)} onMouseLeave={() => setHover(0)}
          style={{ fontSize: 28, cursor: 'pointer', color: star <= (hover || value) ? '#BA7517' : '#E0E0E0', transition: 'color 0.1s' }}>
          ★
        </span>
      ))}
    </div>
  );
}

const getRatingLabel = (val) => ['', 'Décevant', 'Passable', 'Bien', 'Très bien', 'Exceptionnel'][val] || '';

export default function ReviewPage({ onNavigate }) {
  const [role, setRole] = useState('voyageur');
  const [globalRating, setGlobalRating] = useState(0);
  const [criteriaRatings, setCriteriaRatings] = useState({});
  const [comment, setComment] = useState('');
  const [privateNote, setPrivateNote] = useState('');
  const [recommend, setRecommend] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [recentBooking, setRecentBooking] = useState(null);
  const [hasEligibleBooking, setHasEligibleBooking] = useState(false);
  const [alreadyReviewed, setAlreadyReviewed] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) { setLoading(false); return; }
      setUser(session.user);
      const uid = session.user.id;

      // Chercher une réservation en checkout pour ce voyageur
      const now = new Date().toISOString();
      const { data: booking } = await supabase
        .from('bookings')
        .select('*, listings(*)')
        .eq('guest_id', uid)
        .in('status', ['checkout', 'confirmed'])
        .lt('date_end', now)
        .order('date_end', { ascending: false })
        .limit(1)
        .single();

      if (!booking) {
        // Vérifier si c'est un hôte avec des séjours terminés
        const { data: hostBooking } = await supabase
          .from('bookings')
          .select('*, listings(*)')
          .eq('host_id', uid)
          .in('status', ['checkout', 'confirmed'])
          .lt('date_end', now)
          .order('date_end', { ascending: false })
          .limit(1)
          .single();

        if (hostBooking) {
          setRecentBooking(hostBooking);
          setRole('hote');
          // Vérifier si déjà évalué
          const { data: existing } = await supabase
            .from('reviews')
            .select('id')
            .eq('booking_id', hostBooking.id)
            .eq('host_id', uid)
            .single();
          setAlreadyReviewed(!!existing);
          setHasEligibleBooking(true);
        } else {
          setHasEligibleBooking(false);
        }
      } else {
        setRecentBooking(booking);
        setRole('voyageur');
        // Vérifier si déjà évalué
        const { data: existing } = await supabase
          .from('reviews')
          .select('id')
          .eq('booking_id', booking.id)
          .eq('guest_id', uid)
          .single();
        setAlreadyReviewed(!!existing);
        setHasEligibleBooking(true);
      }
      setLoading(false);
    });
  }, []);

  const criteria = role === 'voyageur' ? CRITERIA_VOYAGEUR : CRITERIA_HOTE;
  const setCriteria = (id, val) => setCriteriaRatings(prev => ({ ...prev, [id]: val }));
  const allCriteriaFilled = criteria.every(c => criteriaRatings[c.id] > 0);
  const canSubmit = globalRating > 0 && allCriteriaFilled && comment.length >= 20;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    if (!user) { onNavigate('auth'); return; }
    setLoading(true);
    setError(null);
    try {
      const reviewData = {
        booking_id:           recentBooking?.id || null,
        listing_id:           recentBooking?.listing_id || null,
        guest_id:             role === 'voyageur' ? user.id : recentBooking?.guest_id || null,
        host_id:              role === 'hote' ? user.id : recentBooking?.host_id || null,
        reviewer_type:        role,
        visible:              false, // Caché jusqu'à ce que les deux aient soumis
        expires_at:           new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 jours
        rating_overall:       globalRating,
        rating_cleanliness:   criteriaRatings['proprete'] || null,
        rating_accuracy:      criteriaRatings['conformite'] || null,
        rating_communication: criteriaRatings['communication'] || null,
        rating_location:      criteriaRatings['localisation'] || null,
        rating_value:         criteriaRatings['rapport'] || null,
        comment:              comment,
      };

      const { error: err } = await supabase.from('reviews').insert(reviewData);
      if (err) throw err;

      // Vérifier si l'autre partie a déjà soumis son avis
      const otherType = role === 'voyageur' ? 'hote' : 'voyageur';
      const { data: otherReview } = await supabase
        .from('reviews')
        .select('id')
        .eq('booking_id', recentBooking?.id)
        .eq('reviewer_type', otherType)
        .single();

      // Si les deux ont soumis → rendre les deux visibles
      if (otherReview) {
        await supabase
          .from('reviews')
          .update({ visible: true })
          .eq('booking_id', recentBooking?.id);

        // Mettre à jour la note de l'annonce
        if (recentBooking?.listing_id) {
          const { data: allReviews } = await supabase
            .from('reviews')
            .select('rating_overall')
            .eq('listing_id', recentBooking.listing_id)
            .eq('visible', true)
            .eq('reviewer_type', 'voyageur');
          if (allReviews?.length) {
            const avg = allReviews.reduce((s, r) => s + r.rating_overall, 0) / allReviews.length;
            await supabase.from('listings')
              .update({ score_transparency: Math.min(100, Math.round(avg * 20)) })
              .eq('id', recentBooking.listing_id);
          }
        }
      }

      setAlreadyReviewed(true);
    } catch (e) {
      console.error('Review error:', e);
      setError("Une erreur est survenue. Veuillez réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const listingTitle = recentBooking?.listings?.title || 'Villa provençale avec piscine';
  const listingLocation = recentBooking?.listings?.city || 'Nice, Côte d\'Azur';
  const bookingDates = recentBooking
    ? `${new Date(recentBooking.date_start).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} – ${new Date(recentBooking.date_end).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}`
    : '12-19 juillet 2025';

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Vérification de votre réservation...</div>
    </div>
  );

  if (!user) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ background: 'var(--white)', borderRadius: 20, padding: '2.5rem', maxWidth: 440, width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, marginBottom: 12 }}>Connexion requise</h2>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 24, lineHeight: 1.6 }}>Vous devez être connecté pour laisser un avis.</p>
        <button onClick={() => onNavigate('auth')} style={{ padding: '11px 28px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
          Se connecter
        </button>
      </div>
    </div>
  );

  if (!hasEligibleBooking) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ background: 'var(--white)', borderRadius: 20, padding: '2.5rem', maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 16 }}>🏠</div>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, marginBottom: 12 }}>Avis non disponible</h2>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 8, lineHeight: 1.7 }}>
          Vous pouvez laisser un avis uniquement après avoir séjourné dans un logement Havenly.
        </p>
        <p style={{ fontSize: 13, color: 'var(--ink-4)', marginBottom: 24, lineHeight: 1.6 }}>
          Un email vous sera envoyé automatiquement 24h après votre check-out pour vous inviter à laisser votre avis.
        </p>
        <button onClick={() => onNavigate('search')} style={{ padding: '11px 28px', background: 'var(--ink)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
          Explorer les logements
        </button>
      </div>
    </div>
  );

  if (alreadyReviewed) return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
      <div style={{ background: 'var(--white)', borderRadius: 20, padding: '2.5rem', maxWidth: 480, width: '100%', textAlign: 'center' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: '#EEEDFE', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#534AB7" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 22, marginBottom: 12 }}>Avis déjà soumis</h2>
        <p style={{ fontSize: 14, color: 'var(--ink-3)', marginBottom: 8, lineHeight: 1.7 }}>
          Vous avez déjà laissé un avis pour ce séjour.
        </p>
        <p style={{ fontSize: 13, color: 'var(--ink-4)', marginBottom: 24, lineHeight: 1.6 }}>
          Votre avis sera visible dès que l'autre partie aura soumis le sien, ou automatiquement après 14 jours.
        </p>
        <button onClick={() => onNavigate('home')} style={{ padding: '11px 28px', background: 'var(--ink)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
          Retour à l'accueil
        </button>
      </div>
    </div>
  );
    <div style={{ background: 'var(--surface)', minHeight: '100vh', paddingTop: 60, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ textAlign: 'center', maxWidth: 480, padding: '2rem' }}>
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'var(--green-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--green-mid)" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
        </div>
        <h2 style={{ fontFamily: 'var(--serif)', fontSize: 26, letterSpacing: '-0.5px', marginBottom: 12 }}>Avis soumis !</h2>
        <p style={{ fontSize: 15, color: 'var(--ink-3)', lineHeight: 1.7, marginBottom: 12 }}>
          Votre avis a été enregistré. Il sera publié dès que les deux parties auront validé leur commentaire.
        </p>
        <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 12, padding: '14px 18px', marginBottom: 28, fontSize: 14, color: 'var(--green-mid)', lineHeight: 1.6 }}>
          En attendant la validation de l'autre partie, votre avis reste privé. Vous recevrez une notification par email dès sa publication.
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          <button onClick={() => onNavigate('home')} style={{ padding: '10px 24px', background: 'var(--ink)', color: '#fff', border: 'none', borderRadius: 24, fontSize: 14, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
            Retour à l'accueil
          </button>
          <button onClick={() => onNavigate('dashboard')} style={{ padding: '10px 24px', background: 'transparent', color: 'var(--ink-3)', border: '1px solid var(--border)', borderRadius: 24, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
            Mon dashboard
          </button>
        </div>
      </div>
    </div>
  ;

  return (
    <div style={{ background: 'var(--surface)', minHeight: '100vh', paddingTop: 60 }}>
      <div style={{ background: 'var(--white)', borderBottom: '1px solid var(--border)', padding: '0 2rem' }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0 0' }}>
            <h1 style={{ fontFamily: 'var(--serif)', fontSize: 20, letterSpacing: '-0.5px' }}>Laisser un avis</h1>
            <button onClick={() => onNavigate('home')} style={{ background: 'none', border: 'none', fontSize: 13, color: 'var(--ink-3)', cursor: 'pointer' }}>← Retour</button>
          </div>
          <div style={{ display: 'flex', gap: 0, marginTop: 12 }}>
            {['voyageur', 'hote'].map(r => (
              <button key={r} onClick={() => { setRole(r); setCriteriaRatings({}); }} style={{
                padding: '10px 20px', border: 'none', background: 'transparent',
                fontFamily: 'var(--sans)', fontSize: 13, cursor: 'pointer',
                borderBottom: role === r ? '2px solid var(--ink)' : '2px solid transparent',
                color: role === r ? 'var(--ink)' : 'var(--ink-3)',
                fontWeight: role === r ? 500 : 400,
              }}>
                {r === 'voyageur' ? 'Je suis voyageur' : 'Je suis hôte'}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 680, margin: '0 auto', padding: '2rem' }}>

        {error && (
          <div style={{ background: '#FCEBEB', border: '1px solid #F09595', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#A32D2D' }}>
            {error}
          </div>
        )}

        {!user && (
          <div style={{ background: '#EEEDFE', border: '1px solid #AFA9EC', borderRadius: 12, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: '#534AB7' }}>
            <strong style={{ fontWeight: 500 }}>Connexion requise</strong> — <span style={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={() => onNavigate('auth')}>Connectez-vous</span> pour soumettre un avis.
          </div>
        )}

        {/* Récap réservation */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: 18, marginBottom: 20, display: 'flex', gap: 14, alignItems: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 10, background: 'linear-gradient(135deg, #a8edcb 0%, #6dcfaa 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>🏡</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 500 }}>{listingTitle}</div>
            <div style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}>{listingLocation} · {bookingDates}</div>
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'right' }}>
            {role === 'voyageur' ? 'Hôte : Marie P.' : 'Voyageur : Lucas B.'}
          </div>
        </div>

        {/* Note globale */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>
            {role === 'voyageur' ? 'Note globale du séjour' : 'Note globale du voyageur'}
          </h3>
          <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
            {role === 'voyageur' ? "Comment évaluez-vous votre séjour dans l'ensemble ?" : 'Comment évaluez-vous ce voyageur dans l\'ensemble ?'}
          </p>
          <StarRating value={globalRating} onChange={setGlobalRating} />
          {globalRating > 0 && (
            <div style={{ marginTop: 8, fontSize: 14, fontWeight: 500, color: globalRating >= 4 ? 'var(--green-mid)' : globalRating >= 3 ? 'var(--terra-mid)' : '#A32D2D' }}>
              {getRatingLabel(globalRating)}
            </div>
          )}
        </div>

        {/* Critères */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 20 }}>Critères détaillés</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {criteria.map(c => (
              <div key={c.id}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>{c.label}</div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>{c.desc}</div>
                  </div>
                  {criteriaRatings[c.id] > 0 && (
                    <span style={{ fontSize: 12, fontWeight: 500, color: criteriaRatings[c.id] >= 4 ? 'var(--green-mid)' : criteriaRatings[c.id] >= 3 ? 'var(--terra-mid)' : '#A32D2D' }}>
                      {getRatingLabel(criteriaRatings[c.id])}
                    </span>
                  )}
                </div>
                <StarRating value={criteriaRatings[c.id] || 0} onChange={val => setCriteria(c.id, val)} />
              </div>
            ))}
          </div>
        </div>

        {/* Commentaire */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>Commentaire public</h3>
          <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.6 }}>
            Ce commentaire sera visible par tous une fois que les deux parties auront soumis leur avis.
          </p>
          <textarea value={comment} onChange={e => setComment(e.target.value)}
            placeholder={role === 'voyageur'
              ? "Décrivez votre expérience — le logement, l'accueil, l'ambiance..."
              : 'Décrivez votre expérience avec ce voyageur...'}
            style={{ width: '100%', height: 130, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, fontFamily: 'var(--sans)', outline: 'none', resize: 'vertical', lineHeight: 1.6, color: 'var(--ink)', boxSizing: 'border-box' }}
            onFocus={e => e.target.style.borderColor = 'var(--green)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'} />
          <div style={{ fontSize: 12, color: comment.length < 20 ? 'var(--terra-mid)' : 'var(--green-mid)', marginTop: 6 }}>
            {comment.length} caractères {comment.length < 20 ? `— encore ${20 - comment.length} minimum` : '— bien !'}
          </div>
        </div>

        {/* Note privée */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 16 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>
            Note privée pour Havenly <span style={{ fontSize: 12, color: 'var(--ink-3)', fontWeight: 400 }}>(optionnel)</span>
          </h3>
          <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 14, lineHeight: 1.6 }}>Ces informations ne seront jamais rendues publiques.</p>
          <textarea value={privateNote} onChange={e => setPrivateNote(e.target.value)}
            placeholder="Un problème technique, une suggestion..."
            style={{ width: '100%', height: 80, padding: '12px 14px', border: '1px solid var(--border)', borderRadius: 10, fontSize: 14, fontFamily: 'var(--sans)', outline: 'none', resize: 'vertical', lineHeight: 1.6, color: 'var(--ink)', boxSizing: 'border-box' }}
            onFocus={e => e.target.style.borderColor = 'var(--green)'}
            onBlur={e => e.target.style.borderColor = 'var(--border)'} />
        </div>

        {/* Recommandation */}
        <div style={{ background: 'var(--white)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, marginBottom: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500, marginBottom: 6 }}>
            {role === 'voyageur' ? 'Recommanderiez-vous ce logement ?' : 'Recommanderiez-vous ce voyageur ?'}
          </h3>
          <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
            {[
              { val: true,  label: 'Oui, je recommande', color: 'var(--green)',  bg: 'var(--green-light)', border: 'var(--green)' },
              { val: false, label: 'Non',                 color: '#A32D2D',       bg: '#FCEBEB',            border: '#F09595' },
            ].map(opt => (
              <button key={String(opt.val)} onClick={() => setRecommend(opt.val)} style={{
                flex: 1, padding: '12px', borderRadius: 10,
                border: `1px solid ${recommend === opt.val ? opt.border : 'var(--border)'}`,
                background: recommend === opt.val ? opt.bg : 'var(--white)',
                color: recommend === opt.val ? opt.color : 'var(--ink-3)',
                fontSize: 14, fontWeight: recommend === opt.val ? 500 : 400,
                cursor: 'pointer', fontFamily: 'var(--sans)',
              }}>
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        {/* Info publication */}
        <div style={{ background: 'var(--green-light)', border: '1px solid var(--green)', borderRadius: 12, padding: '14px 18px', marginBottom: 24, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green-mid)" strokeWidth="2" style={{ flexShrink: 0, marginTop: 2 }}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          <p style={{ fontSize: 13, color: 'var(--green-mid)', lineHeight: 1.6, margin: 0 }}>
            <strong style={{ fontWeight: 500 }}>Publication simultanée</strong> — Votre avis sera visible uniquement quand les deux parties auront soumis leur commentaire.
          </p>
        </div>

        {/* Bouton */}
        <button onClick={handleSubmit} disabled={!canSubmit || loading}
          style={{
            width: '100%', padding: '14px', borderRadius: 12,
            background: canSubmit ? 'var(--ink)' : 'var(--border)',
            color: canSubmit ? '#fff' : 'var(--ink-4)',
            border: 'none', fontSize: 15, fontWeight: 500,
            cursor: canSubmit ? 'pointer' : 'not-allowed',
            fontFamily: 'var(--sans)', opacity: loading ? 0.7 : 1,
          }}>
          {loading ? 'Envoi en cours...' : canSubmit ? 'Soumettre mon avis →' : `Complétez tous les critères (${Math.max(0, 20 - comment.length)} car. manquants)`}
        </button>
      </div>
    </div>
  );
}